import vModel from '@/app-core/mixins/vModel'
import mapBoxGl from 'mapbox-gl'
import GeocoderProviderFactory from '@/app-core/lib/maps/geocoder/GeocoderProviderFactory'
import VMap from '@/app-theme/components/map/VMap.vue'
import MapsCommon from '@/app-core/mixins/maps/mapsCommon'
import MapsProviderEnum from '@/app-core/api/enums/MapsProviderEnum'
import {isObject} from '@/app-core/lib/utils/type'
import {pathToValue} from '@/app-core/lib/utils/object'
import GeoLocation from '@/app-core/mixins/geo/geoLocation'
import AddressCommon from '@/app-core/mixins/maps/addressCommon'

export default {
  name      : 'VMapAutocompleteService',
  components: {VMap},
  mixins    : [MapsCommon, GeoLocation, AddressCommon, vModel],
  enums     : {MapsProviderEnum},
  props     : {
    map: {
      type   : Boolean,
      default: false
    },
    addressMarker: {
      type   : Object,
      default: function () {
        return {
          lat: this.AppConfig?.Marketplace?.Location?.Lat || -1,
          lng: this.AppConfig?.Marketplace?.Location?.Lng || -1
        }
      }
    },
    storeMarker: {
      type   : Object,
      default: function () {
        return {
          lat: this.AppConfig?.Marketplace?.Location?.Lat || -1,
          lng: this.AppConfig?.Marketplace?.Location?.Lng || -1
        }
      }
    },
    geolocate: {
      type   : Boolean,
      default: false
    },
    mapZoom: {
      type   : Number,
      default: 17
    },
    mapStyles: {
      type   : Boolean,
      default: true
    },
    mapWidth: {
      type   : String,
      default: '100%'
    },
    mapHeight: {
      type   : String,
      default: '355px'
    },
    mapRef: {
      type   : Object,
      default: undefined
    },
    autocomplete: {
      type   : Boolean,
      default: true
    },
    autofocus: {
      type   : Boolean,
      default: false
    },
    disabled: {
      type   : Boolean,
      default: false
    },
    readonly: {
      type   : Boolean,
      default: false
    },
    dark: {
      type   : Boolean,
      default: false
    },
    lazy: {
      type   : Boolean,
      default: true
    },
    clearIconCb: {
      type   : Function,
      default: () => {
      }
    },
    placeholder: {
      type   : String,
      default: () => this.$t('Checkout.Details.DeliveryDetails.Field.Autocomplete.Label')
    },
    color: {
      type   : String,
      default: 'primary'
    },
    hint: {
      type   : String,
      default: ''
    },
    errorMessages: {
      type   : [String, Array],
      default: () => []
    },
    rules: {
      type   : Array,
      default: () => []
    },
    country: {
      type   : String,
      default: 'gr'
    },
    language: {
      type   : String,
      default: 'el'
    },
    types: {
      type   : Array,
      default: () => [],
      validator(value) {
        const validValues = ['geocode', 'address', 'city', 'postcode']
        return value?.some(v => validValues.includes(v))
      }
    },
    displayField: {
      type   : String,
      default: 'Name'
    },
    debounceTimeout: {
      type   : Number,
      default: 1000
    },
    debounceMinChars: {
      type   : Number,
      default: 3
    }
  },
  data() {
    return {
      modelStr                   : null,
      mapObj                     : null,
      markerLatLng               : null,
      autocompleteDebounceTimeout: null,
      locationSearchText         : null,
      loading                    : false,
      locationEntries            : []
    }
  },
  computed: {
    locationFoundItems() {
      return this.locationEntries || []
    }
  },
  watch: {
    addressMarker(newVal) {
      this.markerLatLng = newVal
    },
    vModel: {
      handler(newVal) {
        if (newVal.Provider === MapsProviderEnum.Google && newVal.LatLng.lat === -1 && newVal.LatLng.lng === -1) {
          this.getPlaceDetails(newVal)
        } else {
          this.$set(this, 'modelStr', pathToValue(this.displayField, newVal) || '')
          this.$emit('place-changed', newVal || null)
          this.gotoMarker(newVal)
        }

      },
      deep     : true,
      immediate: true
    },

    locationSearchText(newVal, oldVal) {
      const newSearchStr = newVal && newVal.trim() || ''
      const oldSearchStr = oldVal && oldVal.trim() || ''

      if ((this.lazy && this.modelStr === newSearchStr) || this.disabled) return

      if (newSearchStr !== oldSearchStr && (newSearchStr.length >= this.debounceMinChars || newSearchStr.length === 0)) {
        clearTimeout(this.autocompleteDebounceTimeout)
        this.autocompleteDebounceTimeout = setTimeout(() => {
          this.locationSearchTextDebounced(newSearchStr)
        }, this.debounceTimeout)
      }
    }
  },
  created() {
    mapBoxGl.accessToken = this.interactiveMapsProvider.Key
    this.markerLatLng = this.addressMarker || this.storeMarker
  },
  mounted() {
  },
  beforeDestroy() {
  },
  methods: {
    onBlur(val) {
      this.$emit('blur', val)
    },

    onFocus(val) {
      this.$emit('focus', val)
    },

    onInput(val) {
      if (isObject(val) || val === null) {
        this.$emit('input', val)
      }
    },

    onChange(val) {
      if (isObject(val) || val === null) {
        this.vModel = val
        this.$emit('change', val || null)
      }
    },

    gotoMarker(place) {
      if (!this.map || !this.mapObj?.Instance) return

      this.markerLatLng = place ? place.LatLng : this.storeMarker
    },

    async onMarkerDragEnd(e, latLng) {
      await this.getAddressFromLatLng(latLng)
    },

    async getAddressFromLatLng(latLng) {
      this.loading = true

      const result = await GeocoderProviderFactory(this.geocodeProvider.Name, {
        accessToken : this.geocodeProvider.Key,
        autoComplete: this.autocomplete,
        country     : this.country,
        language    : this.language,
        forwardTypes: this.types
      }).geocodeReverse(latLng)

      if (result) {
        result.LatLng = latLng
        result.Center = [latLng.lng, latLng.lat]
      }

      this.vModel = result

      this.loading = false

      return result
    },

    async locationSearchTextDebounced(searchStr) {
      if (!searchStr || this.disabled) return

      this.loading = true

      const result = await GeocoderProviderFactory(this.geocodeProvider.Name, {
        accessToken : this.geocodeProvider.Key,
        autoComplete: this.autocomplete,
        country     : this.country,
        language    : this.language,
        forwardTypes: this.types
      }).geocodeSearch(searchStr)

      if (this.autocomplete) {
        this.locationEntries = result
      } else {
        this.vModel = result
      }

      this.loading = false
    },

    async getPlaceDetails(place) {
      if (!place || this.disabled) return

      this.loading = true

      const result = await GeocoderProviderFactory(this.geocodeProvider.Name, {
        input      : this.$refs?.autocompleteServiceComponent?.$refs?.input,
        accessToken: this.geocodeProvider.Key,
        language   : this.language
      }).placeDetails(place.Id)

      this.vModel = result

      this.loading = false
    },

    async getAddressDetails(place) {
      if (!place || this.disabled) return

      this.loading = true

      const result = await GeocoderProviderFactory(this.geocodeProvider.Name, {
        accessToken: this.geocodeProvider.Key,
        language   : this.language
      }).addressDetails(place.Name)

      this.vModel = result

      this.loading = false
    },

    async geolocateUser() {
      if (this.loading || !this.geolocate || !this.isGeoLocationAvailable()) return

      this.loading = true

      try {
        const latlng = await this.getGeoLocation({
          enableHighAccuracy: true,
          maximumAge        : 15000,
          timeout           : 60000
        })
        const place = {LatLng: latlng, Center: [latlng.lng, latlng.lat]}
        const isWithinDeliveryAreas = await this.isPlaceInsideDeliveryAreas(place)

        if (isWithinDeliveryAreas) {
          await this.getAddressFromLatLng(latlng)
        } else {
          this.notifyUserOutOfBoundsAddress(true)
        }

      } catch (e) {
        console.error(e)
        this.loading = false
      }

      this.loading = false
    }
  }
}
